import type { ComponentType } from 'react';
import { createElement, useCallback } from 'react';
import { useToggle } from '@trinity/chat-components/hooks/useToggle';
import type { UseModalProps } from '../interfaces';

type Props = UseModalProps;

type Options = {
  keepMounted?: boolean;
};

export const useModal = <T extends Props = Props, K = Omit<T, 'onClose' | 'open'>>(ModalComponent: ComponentType<Props & K>, options: Options = {}) => {
  const [visible, toggleVisibility] = useToggle();

  const Modal = useCallback((props: K) => {
    if (!options.keepMounted && !visible) {
      return null;
    }

    // `Props & K` can't be re-derived by TS from `{ onClose, open } & K` when K is an
    // unconstrained generic, and JSX's LibraryManagedAttributes check on a generically-typed
    // component hits the same wall — build the props explicitly and use createElement instead
    // of JSX to sidestep that check.
    const modalProps = {
      onClose: toggleVisibility,
      open: visible,
      ...props,
    } as Props & K;

    return createElement(ModalComponent, modalProps);
  }, [
    ModalComponent,
    options.keepMounted,
    toggleVisibility,
    visible,
  ]);

  /* eslint-disable-next-line */
  // @ts-ignore
  Modal.displayName = `useModal`;

  return [toggleVisibility, Modal, visible] as const;
};